import React, { useEffect, useState } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Grid,
    MenuItem,
    IconButton,
    Stack,
    Paper,
    alpha,
    Autocomplete,
} from "@mui/material";
import {
    CloudUpload,
    Send,
    AttachMoney,
    DateRange,
    LocationOn,
    Work,
    Description,
    Cancel
} from "@mui/icons-material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import axios from "axios";
import baseURL from "../../config";
import { useParams } from "react-router-dom";
import bgImage from '../../assets/shipimage.jpg';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddVacancy = () => {
    const [formData, setFormData] = useState({
        title: "",
        vesselType: "",
        rank: "",
        joiningDate: "",
        salary: { min: "", max: "", currency: "USD" },
        contractDuration: "",
        requirements: "",
        description: "",
        location: "",
        img: null,
        vessel: null, // vessel object
    });

    const [vesselOwner, setVesselOwner] = useState(null);
    const [rankData, setRankData] = useState([]);
    const [vesselList, setVesselList] = useState([]);
    const [error, setError] = useState(null);
    const { id } = useParams();

    useEffect(() => {
        const fetchVesselOwnerDetails = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${baseURL}/api/vesselDetails/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setVesselOwner(response.data.data);
            } catch (err) {
                setError("Failed to fetch vessel owner details.");
                console.error("Error fetching vessel owner details:", err);
            }
        };
        fetchVesselOwnerDetails();
    }, [id]);

    useEffect(() => {
        const fetchRankData = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${baseURL}/api/rankData`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setRankData(response.data.data)
            } catch (err) {
                setError("Failed to fetch rank data.");
                console.error("Error fetching rank data:", err);
            }
        };
        fetchRankData();
    }, []);

    // Fetch vessel list for Autocomplete
    useEffect(() => {
        const fetchVesselList = async () => {
            try {
                const vesselownerId = localStorage.getItem("urlID");
                const token = localStorage.getItem("token");
                const response = await axios.get(
                    `${baseURL}/api/vesselOwner/getshipdetailsbyvesselownerId/${vesselownerId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setVesselList(response.data.data || []);
            } catch (err) {
                setError("Failed to fetch vessel list.");
                console.error("Error fetching vessel list:", err);
            }
        };
        fetchVesselList();
    }, []);

    const handleRankChange = (event, newValue) => {
        setFormData((prev) => ({
            ...prev,
            rank: newValue || ""
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (["min", "max", "currency"].includes(name)) {
            setFormData((prev) => ({
                ...prev,
                salary: { ...prev.salary, [name]: value },
            }));
        } else if (name === "isActive") {
            setFormData((prev) => ({ ...prev, isActive: checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = (e) => {
        setFormData((prev) => ({ ...prev, img: e.target.files[0] }));
    };

    const removeImage = () => {
        setFormData((prev) => ({ ...prev, img: null }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const userId = localStorage.getItem("urlID");

        const data = new FormData();
        data.append("title", formData.title); // vesselname will be set as title
        data.append("vesselType", formData.vesselType);
        data.append("rank", formData.rank);
        data.append("joiningDate", formData.joiningDate);
        data.append("salaryMin", formData.salary.min);
        data.append("salaryMax", formData.salary.max);
        data.append("salaryCurrency", formData.salary.currency);
        data.append("contractDuration", formData.contractDuration);
        data.append(
            "requirements",
            formData.requirements
                ? formData.requirements
                    .split("\n")
                    .map((req) => req.trim())
                    .filter((req) => req !== "")
                    .join(",")
                : ""
        );
        data.append("description", formData.description);
        data.append("location", formData.location);
        data.append("postedById", userId);
        data.append("postedByName", vesselOwner?.company_name || "");
        if (formData.img) data.append("file", formData.img);

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${baseURL}/api/vacancy`, data, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: token ? `Bearer ${token}` : "",
                },
            });

            toast.success("Vacancy created successfully!");
            setFormData({
                title: "",
                vesselType: "",
                rank: "",
                joiningDate: "",
                salary: { min: "", max: "", currency: "USD" },
                contractDuration: "",
                requirements: "",
                description: "",
                location: "",
                img: null,
                vessel: null,
            });
        } catch (err) {
            toast.error(
                `Error creating vacancy: ${err.response?.data?.message || err.message}`
            );
        }
    };

    // Prepare rank options for autocomplete
    const rankOptions = rankData
        .filter(rank => rank.rankname && rank.rankname.trim() !== "")
        .map(rank => rank.rankname)
        .sort();

    const filterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option) => option.toLowerCase(),
    });

    return (
        <>
            <Box
                sx={{
                    minHeight: "100vh",
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    py: 4,
                    px: 2,
                }}
            >
                <Box sx={{ maxWidth: 900, mx: "auto" }}>
                    {/* Header */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 4,
                            borderRadius: 4,
                            background: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(20px)",
                            textAlign: "center"
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 900,
                                background: "red",
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                mb: 1
                            }}
                        >
                            Create New Vacancy
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Post your maritime vacancy
                        </Typography>
                    </Paper>

                    {error && (
                        <Paper
                            sx={{
                                p: 3,
                                mb: 3,
                                borderRadius: 3,
                                background: alpha("#f44336", 0.1),
                                border: "1px solid #f44336"
                            }}
                        >
                            <Typography color="error" fontWeight="medium">
                                {error}
                            </Typography>
                        </Paper>
                    )}

                    <Paper
                        elevation={20}
                        sx={{
                            borderRadius: 4,
                            overflow: "hidden",
                            background: "rgba(255, 255, 255, 0.98)",
                            backdropFilter: "blur(20px)"
                        }}
                    >
                        <Box component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
                            <Grid container spacing={4}>
                                {/* Job Details Section */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 3,
                                            fontWeight: 700,
                                            color: "#1a1a1a"
                                        }}
                                    >
                                        <Work sx={{ color: "#C30B12" }} />
                                        Vacancy Details
                                    </Typography>
                                </Grid>

                                {/* Vessel Autocomplete */}
                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={vesselList}
                                        getOptionLabel={(option) => option.vesselname || "Unnamed Vessel"}
                                        value={formData.vessel || null}
                                        onChange={(event, newValue) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                vessel: newValue,
                                                title: newValue?.vesselname || "",
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                label="Select Vessel"
                                                placeholder="Choose vessel..."
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: 3,
                                                        backgroundColor: alpha("#667eea", 0.02),
                                                        "&:hover": {
                                                            backgroundColor: alpha("#667eea", 0.05),
                                                        },
                                                        "&.Mui-focused": {
                                                            backgroundColor: alpha("#667eea", 0.08),
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Vessel Type"
                                        name="vesselType"
                                        value={formData.vesselType}
                                        onChange={handleChange}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#667eea", 0.02),
                                                "&:hover": {
                                                    backgroundColor: alpha("#667eea", 0.05),
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Autocomplete
                                        freeSolo
                                        options={rankOptions}
                                        value={formData.rank}
                                        onChange={handleRankChange}
                                        filterOptions={(options, state) =>
                                            options.filter((option) =>
                                                option.toLowerCase().includes(state.inputValue.toLowerCase())
                                            )
                                        }
                                        onInputChange={(event, newInputValue) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                rank: newInputValue || "",
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                label="Rank"
                                                name="rank"
                                                placeholder="Select or type rank..."
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        type="date"
                                        fullWidth
                                        required
                                        label="Joining Date"
                                        name="joiningDate"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.joiningDate}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: <DateRange sx={{ mr: 1, color: "#C30B12" }} />
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#667eea", 0.02),
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Contract Duration"
                                        name="contractDuration"
                                        value={formData.contractDuration}
                                        onChange={handleChange}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#667eea", 0.02),
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Salary Section */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 3,
                                            mt: 2,
                                            fontWeight: 700,
                                            color: "#1a1a1a"
                                        }}
                                    >
                                        <AttachMoney sx={{ color: "#C30B12" }} />
                                        Compensation
                                    </Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="number"
                                        label="Minimum Salary"
                                        name="min"
                                        value={formData.salary.min}
                                        onChange={handleChange}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#4caf50", 0.02),
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="number"
                                        label="Maximum Salary"
                                        name="max"
                                        value={formData.salary.max}
                                        onChange={handleChange}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#4caf50", 0.02),
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Currency"
                                        name="currency"
                                        value={formData.salary.currency}
                                        onChange={handleChange}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#4caf50", 0.02),
                                            }
                                        }}
                                    >
                                        <MenuItem value="USD">USD ($)</MenuItem>
                                        <MenuItem value="INR">INR (₹)</MenuItem>
                                        <MenuItem value="EUR">EUR (€)</MenuItem>
                                        <MenuItem value="AED">AED (د.إ)</MenuItem>
                                        <MenuItem value="GBP">GBP (£)</MenuItem>
                                        <MenuItem value="AUD">AUD (A$)</MenuItem>
                                        <MenuItem value="CAD">CAD (C$)</MenuItem>
                                    </TextField>
                                </Grid>

                                {/* Job Description Section */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 3,
                                            mt: 2,
                                            fontWeight: 700,
                                            color: "#1a1a1a"
                                        }}
                                    >
                                        <Description sx={{ color: "#C30B12" }} />
                                        Vacancy Information
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Requirements(one per line)"
                                        name="requirements"
                                        value={formData.requirements}
                                        onChange={handleChange}
                                        placeholder="• Valid COC Certificate&#10;• STCW Basic Safety Training&#10;• Tanker Familiarization"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#ff9800", 0.02),
                                                "& textarea": {
                                                    fontSize: "14px",
                                                    lineHeight: 1.6
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Vacancy Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Describe the role, responsibilities, and what makes this opportunity special..."
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#2196f3", 0.02),
                                                "& textarea": {
                                                    fontSize: "14px",
                                                    lineHeight: 1.6
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Location"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: <LocationOn sx={{ mr: 1, color: "#C30B12" }} />
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: alpha("#9c27b0", 0.02),
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Image Upload Section */}
                                <Grid item xs={12}>
                                    <Box sx={{ position: "relative" }}>
                                        {!formData.img ? (
                                            <Paper
                                                sx={{
                                                    p: 4,
                                                    borderRadius: 3,
                                                    border: "2px dashed #C30B12",
                                                    background: alpha("#667eea", 0.02),
                                                    textAlign: "center",
                                                    cursor: "pointer",
                                                    transition: "all 0.3s ease",
                                                    "&:hover": {
                                                        background: alpha("#667eea", 0.05),
                                                        borderColor: "#5a6fd8",
                                                        transform: "translateY(-2px)"
                                                    }
                                                }}
                                                component="label"
                                            >
                                                <CloudUpload sx={{ fontSize: 48, color: "#C30B12", mb: 2 }} />
                                                <Typography variant="h6" color="#C30B12" fontWeight="600">
                                                    Upload Vacancy Image
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" mt={1}>
                                                    Upload image in JPG format only, and ensure the size is less than 1 MB.
                                                </Typography>
                                                <input type="file" hidden onChange={handleFileChange} accept="image/*" />
                                            </Paper>
                                        ) : (
                                            <Paper
                                                sx={{
                                                    p: 3,
                                                    borderRadius: 3,
                                                    background: alpha("#4caf50", 0.08),
                                                    border: "1px solid #4caf50"
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Stack direction="row" alignItems="center" gap={2}>
                                                        <CloudUpload sx={{ color: "#4caf50" }} />
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="600" color="#2e7d32">
                                                                {formData.img.name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {(formData.img.size / 1024 / 1024).toFixed(2)} MB
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                    <IconButton
                                                        onClick={removeImage}
                                                        sx={{
                                                            color: "#f44336",
                                                            "&:hover": {
                                                                backgroundColor: alpha("#f44336", 0.1)
                                                            }
                                                        }}
                                                    >
                                                        <Cancel />
                                                    </IconButton>
                                                </Stack>
                                            </Paper>
                                        )}
                                    </Box>
                                </Grid>

                                {/* Submit Section */}
                                <Grid item xs={12}>
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        mt: 4,
                                        gap: 2
                                    }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            endIcon={<Send />}
                                            sx={{
                                                px: 6,
                                                py: 2,
                                                fontSize: "18px",
                                                fontWeight: "bold",
                                                borderRadius: 4,
                                                background: "linear-gradient(45deg, #C30B12, #ff1744)",
                                                boxShadow: "0 8px 32px rgba(195, 11, 18, 0.3)",
                                                textTransform: "none",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    background: "linear-gradient(45deg, #a00, #d32f2f)",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 12px 40px rgba(195, 11, 18, 0.4)"
                                                },
                                                "&:active": {
                                                    transform: "translateY(0px)"
                                                }
                                            }}
                                        >
                                            Publish Vacancy
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Box>
            </Box>
            <ToastContainer />
        </>
    );
};

export default AddVacancy;